export interface UploadDrainWakePolicy {
	start(wake: (reason: string) => void, isDraining: () => boolean): void;
	stop(): void;
	pendingObserved(): void;
	idleObserved(observedAt: number): void;
	claimMissed(): void;
}

interface UploadDrainEngineOptions<Session, Context = void> {
	beforeDrain?(reason: string): Promise<Context>;
	reclaimStartup(): Promise<void>;
	discoverPending(context: Context): Promise<Session[]>;
	claimDrain(context: Context, run: () => Promise<boolean>): Promise<boolean | undefined>;
	resumeSession(session: Session, context: Context): Promise<void>;
	/**
	 * LSP-17008: how many pending sessions may resume at once. Defaults to 1 — strictly sequential,
	 * byte-identical to the pre-option behavior — so a consumer only gets lanes by opting in.
	 * contact-async passes its file-upload lane count so an offline-recovery backlog drains at the
	 * same width as a fresh pick instead of crawling one file at a time.
	 */
	resumeConcurrency?: number;
	/**
	 * Stable identity for a pending session, used as the pool's in-flight de-duplication key.
	 * Defaults to the row id (`session.id`, or the session itself when it is already a string), which
	 * is what every consumer's durable row is keyed by.
	 *
	 * The engine drains through a replenishing pool: a `drainNow` that arrives while lanes are busy is
	 * honored immediately rather than after the open batch settles, so a session enqueued while a
	 * sibling's request is in flight starts in one of the free `resumeConcurrency` lanes instead of
	 * waiting for that request to finish. The in-flight key set provides the de-duplication that the
	 * batch-at-a-time await used to provide, so a session is never admitted twice concurrently.
	 *
	 * The pool keeps the old batch loop's discovery *cadence*: exactly one `discoverPending` per
	 * drain-request boundary, never a re-scan merely because a lane freed. A session that only becomes
	 * eligible part-way through a pass therefore still waits for the next drain request, as it did
	 * under the batch loop.
	 *
	 * Two consequences relative to the batch loop, both deliberate:
	 * - `pendingObserved` is reported when pending work is *observed*, not after a batch settles, and
	 *   `idleObserved` is withheld while lanes are still open (the batch loop could never observe that
	 *   state because it never re-discovered mid-pass).
	 * - `afterDrain` receives the sessions the pass actually admitted rather than every session it
	 *   discovered, so a session deferred for want of a free lane is not double-counted.
	 */
	sessionKey?(session: Session): string;
	/**
	 * LSP-21610: optional per-admission gate. Called for each queued session in queue order with the
	 * sessions currently in flight and the rest of the queue; the first one admitted takes the lane.
	 * When none is admitted the pass parks until a lane frees, a drain request lands, or `reevaluate()`
	 * is called. `resumeConcurrency` stays the hard cap. Absent, admission is the plain `shift()`.
	 */
	admit?(session: Session, inFlight: readonly Session[], queued: readonly Session[], context: Context): boolean;
	afterDrain?(context: Context, sessions: Session[]): Promise<void>;
	onSessionError?(session: Session, error: unknown, context: Context): void | Promise<void>;
	onSessionErrorHandlerError?(error: unknown, session: Session, context: Context): void | Promise<void>;
	onDrainError?(error: unknown): void;
	/**
	 * LSP-21610: a consumer that can hot-loop on a deterministic admission/discovery failure may opt in to NOT re-running
	 * a drain request that landed while the pass failed. Default (absent) keeps the long-standing behaviour: the
	 * coalesced request is honoured. The drop is reported through `onCoalescedDrainDropped`, never silent.
	 */
	suppressCoalescedAfterFailure?(): boolean;
	onCoalescedDrainDropped?(): void;
	wakePolicy: UploadDrainWakePolicy;
}

export interface UploadDrainEngine {
	start(): Promise<void>;
	drainNow(reason: string): Promise<boolean>;
	/** LSP-21610: re-run `admit` over the already-discovered queue — no re-scan, no new request version. */
	reevaluate(): void;
	dispose(): void;
	isDraining(): boolean;
}

export function createUploadDrainEngine<Session, Context = void>(options: UploadDrainEngineOptions<Session, Context>): UploadDrainEngine {
	let draining = false;
	let drainRequestVersion = 0;
	let drainRequestObservedAt = Date.now();
	let disposed = false;
	let started = false;
	const resumeConcurrency = Math.max(1, Math.floor(options.resumeConcurrency ?? 1));
	// The default is the durable row id. A wrong key is not a cosmetic problem — two distinct rows
	// sharing a key would silently strand one of them for the pass — so an un-derivable identity is a
	// loud programming error rather than a guess.
	const sessionKey =
		options.sessionKey ??
		((session: Session): string => {
			if (typeof session === "string") return session;
			const rowId = (session as { id?: unknown } | null | undefined)?.id;
			// An empty-string row id is still a valid, distinct identity — admin durable rows have been
			// observed with one — so only a genuinely absent id is an error.
			if (typeof rowId === "string") return rowId;
			if (typeof rowId === "number" && Number.isFinite(rowId)) return String(rowId);
			throw new Error("createUploadDrainEngine: pending session has no row id; pass an explicit `sessionKey`.");
		});

	// Lets a drain pass waiting on busy lanes wake on a fresh `drainNow` instead of waiting for a lane
	// to free. Without it a session enqueued while every lane is occupied would still be stalled behind
	// the in-flight predecessor, which is the whole defect the pool fixes.
	let drainRequestWake: (() => void) | undefined;
	const signalDrainRequest = (): void => {
		const wake = drainRequestWake;
		drainRequestWake = undefined;
		wake?.();
	};

	// Resume one session with full error containment: a throwing resumeSession — or a throwing
	// onSessionError / onSessionErrorHandlerError — must never escape, because that would starve the
	// rest of the pending batch and skip afterDrain (e.g. admin's handler touches an unmounted dialog
	// ref during a startup/background drain and throws TypeError).
	const resumeOne = async (session: Session, context: Context): Promise<void> => {
		try {
			await options.resumeSession(session, context);
		} catch (error) {
			try {
				await options.onSessionError?.(session, error, context);
			} catch (handlerError) {
				try {
					if (options.onSessionErrorHandlerError) {
						await options.onSessionErrorHandlerError(handlerError, session, context);
					} else {
						await options.onDrainError?.(handlerError);
					}
				} catch {
					// Diagnostic callbacks cannot be allowed to escape the per-session isolation
					// boundary and starve sibling uploads.
				}
			}
		}
	};

	// Block until the first lane frees OR a fresh drain request lands. `observedVersion` is
	// the request version read before discovery ran, so a request that arrived *during* the discovery
	// await is not lost — it short-circuits the wait and re-discovers instead.
	const waitForFreeLaneOrDrainRequest = async (lanes: Promise<void>[], observedVersion: number): Promise<void> => {
		if (drainRequestVersion !== observedVersion) return;
		const nextDrainRequest = new Promise<void>((resolve) => {
			drainRequestWake = resolve;
		});
		try {
			await Promise.race([...lanes, nextDrainRequest]);
		} finally {
			drainRequestWake = undefined;
		}
	};

	const engine: UploadDrainEngine = {
		async start(): Promise<void> {
			if (started || disposed) return;
			started = true;
			try {
				options.wakePolicy.start(
					(reason) => {
						void engine.drainNow(reason);
					},
					() => draining
				);
				await options.reclaimStartup();
				await engine.drainNow("startup");
			} catch (error) {
				started = false;
				if (!disposed) options.wakePolicy.stop();
				throw error;
			}
		},
		async drainNow(reason: string): Promise<boolean> {
			if (disposed) return false;
			if (draining) {
				drainRequestVersion += 1;
				drainRequestObservedAt = Date.now();
				// Releases a pass parked on busy lanes so the newly enqueued work is admitted into a free
				// lane immediately.
				signalDrainRequest();
				return false;
			}
			draining = true;
			let handledDrainRequestVersion = drainRequestVersion;
			let handledDrainRequestObservedAt = Date.now();
			let observedPending = false;
			let drainFailed = false;
			try {
				const context = options.beforeDrain ? await options.beforeDrain(reason) : (undefined as Context);
				if (disposed) return false;
				const drainedSessions: Session[] = [];
				const claimed = await options.claimDrain(context, async () => {
					// Replenishing pool — the engine's only drain loop. The discovery cadence matches the
					// batch-at-a-time loop this replaced: one `discoverPending` per drain-request boundary,
					// never a re-scan merely because a lane freed, so a session that only becomes eligible
					// mid-pass still waits for the next drain request. What changed is that a boundary is
					// honored the moment the request arrives instead of after the open batch settles, and
					// that discovered work beyond the free lanes waits in `queuedSessions` rather than
					// inside a `processWithConcurrency` call the loop had to await. `inFlightSessions`,
					// keyed by `sessionKey`, is the in-flight de-duplication that batch await used to
					// provide — the sole reason re-discovery had to wait for a batch boundary at all.
					const inFlightSessions = new Map<string, Promise<void>>();
					// LSP-21610: the in-flight sessions themselves, for the `admit` gate.
					const inFlightByKey = new Map<string, Session>();
					let queuedSessions: Session[] = [];
					let discoveryPending = true;
					let discoverySkippedInFlightSession = false;
					// The batch loop reported idle from inside discovery because it could never be holding
					// open lanes at that moment. The pool can, so an empty mid-pass scan defers its idle
					// report to the exit below rather than dropping it — a lost `idleObserved` silently
					// removes the wake policy's only signal that the queue actually drained.
					let scanFoundPending = false;
					let scanReportedIdle = false;
					const settleOpenLanes = async (): Promise<void> => {
						// Teardown stops admitting but lets running lanes settle before the pass returns, so
						// `draining` is never cleared while a lane is still writing.
						await Promise.all([...inFlightSessions.values()]);
					};
					try {
						for (;;) {
							if (disposed) {
								await settleOpenLanes();
								return observedPending;
							}
							const versionBeforeDiscovery = drainRequestVersion;
							if (discoveryPending) {
								discoveryPending = false;
								discoverySkippedInFlightSession = false;
								// Discovery is the source of truth: anything still queued from the previous
								// boundary is superseded by this scan, matching the batch loop's re-discovery.
								queuedSessions = (await options.discoverPending(context)).filter((session) => {
									if (!inFlightSessions.has(sessionKey(session))) return true;
									// The row is already being resumed, so it cannot be admitted again — but the
									// row this key names may have been replaced underneath the running attempt
									// (Live's durable snapshot replacement is exactly that). Leave the drain
									// request outstanding so the loop re-scans once every lane has settled, which
									// is where the batch loop always looked at it. Re-scanning earlier would race
									// the running attempt's own terminal write and could double-upload it.
									discoverySkippedInFlightSession = true;
									return false;
								});
								if (!discoverySkippedInFlightSession && handledDrainRequestVersion !== versionBeforeDiscovery) {
									// This scan saw the complete pending set, so it fully answers every request up
									// to `versionBeforeDiscovery`.
									handledDrainRequestVersion = versionBeforeDiscovery;
									handledDrainRequestObservedAt = drainRequestObservedAt;
								}
								scanFoundPending = queuedSessions.length > 0;
								scanReportedIdle = false;
								if (scanFoundPending) {
									observedPending = true;
									options.wakePolicy.pendingObserved();
								} else if (inFlightSessions.size === 0) {
									options.wakePolicy.idleObserved(handledDrainRequestObservedAt);
									scanReportedIdle = true;
								}
							}
							while (!disposed && queuedSessions.length > 0 && inFlightSessions.size < resumeConcurrency) {
								// LSP-21610: with an `admit` gate the queue is scanned for the first admissible session;
								// without one `index` is 0 and this is the original `shift()`.
								const index = options.admit
									? queuedSessions.findIndex((candidate, position) =>
											options.admit!(
												candidate,
												[...inFlightByKey.values()],
												queuedSessions.filter((_, other) => other !== position),
												context
											)
										)
									: 0;
								if (index < 0) break;
								const [session] = queuedSessions.splice(index, 1);
								const key = sessionKey(session);
								drainedSessions.push(session);
								inFlightByKey.set(key, session);
								inFlightSessions.set(
									key,
									resumeOne(session, context).finally(() => {
										inFlightSessions.delete(key);
										inFlightByKey.delete(key);
									})
								);
							}
							if (disposed) {
								await settleOpenLanes();
								return observedPending;
							}
							if (inFlightSessions.size > 0) {
								await waitForFreeLaneOrDrainRequest([...inFlightSessions.values()], versionBeforeDiscovery);
								// A request that landed while every lane was busy is scanned for now instead of
								// after the open work settles. That is the whole point of the pool — and the only
								// trigger for a mid-pass scan, so a lane merely freeing never re-scans.
								if (drainRequestVersion !== versionBeforeDiscovery) discoveryPending = true;
								continue;
							}
							if (handledDrainRequestVersion === drainRequestVersion) {
								// Every lane has settled and no request is outstanding. If the last scan found
								// nothing and could not report idle because lanes were still open, report it now:
								// this is the moment the pass can prove the queue is empty.
								if (!scanFoundPending && !scanReportedIdle) options.wakePolicy.idleObserved(handledDrainRequestObservedAt);
								break;
							}
							// Every lane has settled and a request is still outstanding — either a brand new one
							// or one whose mid-pass scan had to skip an open lane. Re-scan with nothing in
							// flight, exactly as the batch loop does at its boundary. The version is consumed by
							// that scan, so an adapter whose rows stay discoverable cannot loop here.
							discoveryPending = true;
						}
					} finally {
						// Admission/discovery may throw while a previously admitted lane is still writing.
						if (inFlightSessions.size > 0) await settleOpenLanes();
					}
					if (disposed) return observedPending;
					await options.afterDrain?.(context, drainedSessions);
					return observedPending;
				});
				if (claimed === undefined) options.wakePolicy.claimMissed();
				return claimed ?? false;
			} catch (error) {
				drainFailed = true;
				try {
					options.onDrainError?.(error);
				} catch {
					// Fire-and-forget drain callers must never observe reporter failures.
				}
				return false;
			} finally {
				draining = false;
				if (handledDrainRequestVersion !== drainRequestVersion && !disposed) {
					if (drainFailed && options.suppressCoalescedAfterFailure?.() === true) {
						try {
							options.onCoalescedDrainDropped?.();
						} catch {
							// Reporting must never escape the drain.
						}
					} else void engine.drainNow("coalesced");
				}
			}
		},
		reevaluate(): void {
			// Wakes a pass parked on busy lanes; the version is untouched so the loop re-runs admission on
			// the same queue instead of re-scanning.
			signalDrainRequest();
		},
		dispose(): void {
			if (disposed) return;
			disposed = true;
			// Release a pool-mode pass parked on busy lanes so it reaches its teardown branch without
			// waiting for a lane to free. It still awaits the open lanes there, exactly as batch mode does.
			signalDrainRequest();
			options.wakePolicy.stop();
		},
		isDraining: () => draining
	};
	return engine;
}
